// FixedJournalVerificationPage — 定額仕訳登録ページ (2026-04-22, legacy UI style)
//
// Reader A UI 仕様に従い、JournalVerificationPage と同じ見た目 (KPI / row / expanded / sidePanel /
// bulk action bar / filter bar) を再利用する。データは fixed_amount_recurring_partners × target_payment_month
// を fetch して仮想 JournalEntry に変換し、legacy と同じテーブル UI で表示。
//
// 証憑欄は「証憑なしテンプレート」バナーに置換 (inline PDF, sidepanel PDF tab を削除)。
// 承認ボタン「今月の登録 (1-click)」は coming soon (disabled)、却下ボタンは「今月スキップ」(coming soon)。
//
// 月切替: target_payment_month (支払月ベース, schema-v20)。
//   - Reader B 仕様: next_next_month_N → issue月+1、他は due月。
//   - 尾家 next_next_month_5 は 4/25 払い tab に自然にグループ。
//
// 内部実装: JournalVerificationPage の S (styles) / KPI / row / expanded レイアウトを丸写しにし、
//          入出力のみ fixed-recurring 用に差し替え。column 構成は同じ。

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Container,
  PageTitle,
  SectionTitle,
  SubSectionTitle,
  CardBase,
  ColumnBase,
  HStack,
  VStack,
  Button,
  SearchField,
  SelectBox,
  CheckBox,
  Text,
  Note,
  StatusIcon,
  Breadcrumbs,
  MessageBlock,
  FloatingMessageBlock,
  TabBar,
  ProgressBar,
} from '@freee_jp/vibes';
import {
  previewFixedRecurring,
  fetchFixedRecurringPartners,
  bulkRegisterFixedRecurring,
  openSessionForFixedRecurring,
  skipFixedRecurringMonth,
  FixedRecurringPreview,
  FixedRecurringPartner,
} from '../api/client';
import { fixedJournalStatusToUnified } from '../lib/statusVocab';

// ---------------------------------------------------------------------------
// Types (legacy-compatible JournalEntry shape for UI reuse)
// ---------------------------------------------------------------------------

type FixedStatus = 'pending' | 'registered' | 'skipped' | 'disabled';

interface FixedDetail {
  index: number;
  accountName: string;
  accountItemId: number;
  taxCode: number;
  taxCodeName: string;
  sectionName: string;
  sectionId: number;
  itemName: string | null;
  amount: number;
  taxAmount: number;
  description: string;
}

interface FixedEntry {
  id: string;
  partnerId: number;
  partnerName: string;
  monthlyAmount: number;
  amountStddev: number | null;
  issueDate: string;
  dueDate: string;
  refNumber: string;
  targetPaymentMonth: string | null;
  issueDayPattern: string;
  dueDayPattern: string;
  confidence: number;
  source: string;
  enabled: number;
  lastRegisteredMonth: string | null;
  receiptPresenceRatio: number | null;
  evidenceJson: any;
  details: FixedDetail[];
  storeName: string;
  status: FixedStatus;
  alreadyRegistered: boolean;
  existingFreeeDealId: number | null;
  existingProposalStatus: string | null;
  flags: string[];
  notes: string;
  sampleMonths: string[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// 表示語は統一語彙 (lib/statusVocab.ts fixedJournalStatusToUnified) — pending = 今月分の登録実行を待つ「あなたの番」
const STATUS_OPTIONS = [
  { name: 'すべて', value: 'all' },
  { name: fixedJournalStatusToUnified('pending').label, value: 'pending' },
  { name: fixedJournalStatusToUnified('registered').label, value: 'registered' },
  { name: fixedJournalStatusToUnified('skipped').label, value: 'skipped' },
  { name: fixedJournalStatusToUnified('disabled').label, value: 'disabled' },
];

const SOURCE_OPTIONS = [
  { name: 'すべてのソース', value: 'all' },
  { name: 'ユーザ指定', value: 'user_specified' },
  { name: '動的学習', value: 'dynamic_discovery' },
];

const PATTERN_STATUS_LABELS: Record<string, { label: string; bg: string; color: string }> = {
  Trusted:   { label: '信頼済',   bg: '#e6f4ea', color: '#338650' },
  Reviewed:  { label: 'レビュー済', bg: '#e8f0fe', color: '#2e7cf6' },
  Candidate: { label: '候補',     bg: '#fef7e0', color: '#c67320' },
  Unknown:   { label: '未学習',   bg: '#f5f3f2', color: '#8a8580' },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const statusToIcon = (status: FixedStatus): 'success' | 'warning' | 'error' | 'progress' | 'required' | 'disabled' => {
  switch (status) {
    case 'registered': return 'success';
    case 'pending': return 'required';   // あなたの番 (今月登録の実行待ち)
    case 'skipped': return 'disabled';   // 除外 = グレー中立終端
    case 'disabled': return 'disabled';  // 除外 = グレー中立終端 (赤=失敗事実には使わない)
    default: return 'required';
  }
};

const statusToLabel = (status: FixedStatus): string => fixedJournalStatusToUnified(status).label;

const fmt = (n: number) => '¥' + n.toLocaleString('ja-JP');

const confidenceColor = (c: number) =>
  c >= 0.92 ? '#338650' : c >= 0.75 ? '#c67320' : '#c33939';

const confidenceBarColor = (c: number) =>
  c >= 0.92 ? '#338650' : c >= 0.75 ? '#c67320' : '#c33939';

function currentYyyyMm(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function monthOffset(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number);
  const total = y * 12 + (m - 1) + delta;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  return `${ny}-${String(nm).padStart(2, '0')}`;
}

function patternForConfidence(c: number): 'Trusted' | 'Reviewed' | 'Candidate' | 'Unknown' {
  if (c >= 0.92) return 'Trusted';
  if (c >= 0.75) return 'Reviewed';
  if (c > 0) return 'Candidate';
  return 'Unknown';
}

function patternDescription(pattern: string): string {
  if (pattern === 'month_end') return '月末';
  const fm = pattern.match(/^fixed_(\d+)$/);
  if (fm) return `毎月${fm[1]}日`;
  const sm = pattern.match(/^same_month_(\d+)$/);
  if (sm) return `同月${sm[1]}日`;
  const nm = pattern.match(/^next_month_(\d+)$/);
  if (nm) return `翌月${nm[1]}日`;
  const nnm = pattern.match(/^next_next_month_(\d+)$/);
  if (nnm) return `翌々月${nnm[1]}日`;
  return pattern || '—';
}

// Convert preview × partner → virtual JournalEntry for table UI.
function previewToEntry(p: FixedRecurringPreview, tgtMonth: string): FixedEntry {
  const details: any[] = Array.isArray(p.proposed_deal_json?.details) ? p.proposed_deal_json.details : [];
  const firstSection = details[0]?.sectionName || details[0]?.section_name || '—';
  const alreadyThisMonth = !!p.already_registered_month;
  const disabled = (p.enabled ?? 1) === 0;
  let status: FixedStatus = 'pending';
  if (alreadyThisMonth || p.existing_proposal_status === 'registered' || p.existing_proposal_status === 'approved') {
    status = 'registered';
  } else if (disabled) {
    status = 'disabled';
  }
  const evidence = p.evidence_json || {};
  const sampleMonths: string[] = Array.isArray(evidence.sample_months) ? evidence.sample_months : [];

  return {
    id: `fixed_${p.partner_id}_${tgtMonth}`,
    partnerId: p.partner_id,
    partnerName: p.partner_name || `partner_id: ${p.partner_id}`,
    monthlyAmount: p.monthly_amount,
    amountStddev: p.amount_stddev,
    issueDate: p.issue_date || '',
    dueDate: p.due_date || '',
    refNumber: p.ref_number,
    targetPaymentMonth: p.target_payment_month || null,
    issueDayPattern: p.issue_day_pattern,
    dueDayPattern: p.due_day_pattern,
    confidence: p.confidence,
    source: p.source,
    enabled: p.enabled ?? 1,
    lastRegisteredMonth: p.last_registered_month || null,
    receiptPresenceRatio: p.receipt_presence_ratio ?? null,
    evidenceJson: evidence,
    sampleMonths,
    details: details.map((d: any, idx: number) => ({
      index: idx,
      accountName: d.accountName || d.account_item_name || d.account_name || `#${d.account_item_id || d.accountItemId || 0}`,
      accountItemId: d.account_item_id || d.accountItemId || 0,
      taxCode: d.tax_code || d.taxCode || 0,
      taxCodeName: d.taxCodeName || d.tax_code_name || '—',
      sectionName: d.sectionName || d.section_name || '',
      sectionId: d.section_id || d.sectionId || 0,
      itemName: d.itemName || d.item_name || null,
      amount: d.amount || 0,
      taxAmount: d.taxAmount || d.tax_amount || 0,
      description: d.description || '',
    })),
    storeName: (() => {
      const uniq = Array.from(new Set(
        details.map((d: any) => d.sectionName || d.section_name).filter(Boolean),
      ));
      if (uniq.length === 1) return String(uniq[0]);
      if (uniq.length > 1) return `${uniq.length}店舗按分`;
      return String(firstSection);
    })(),
    status,
    alreadyRegistered: alreadyThisMonth || p.existing_proposal_status === 'registered',
    existingFreeeDealId: p.existing_freee_deal_id,
    existingProposalStatus: p.existing_proposal_status,
    flags: (() => {
      const f: string[] = [];
      if (alreadyThisMonth) f.push('今月登録済');
      else if (p.existing_proposal_status === 'approved') f.push('承認済');
      if (disabled) f.push('対象外');
      if (p.source === 'user_specified') f.push('ユーザ確証');
      return f;
    })(),
    notes: '',
  };
}

// ---------------------------------------------------------------------------
// Styles (CSS-in-JS objects) — cloned from JournalVerificationPage
// ---------------------------------------------------------------------------

const S = {
  page: {
    background: '#f7f5f5',
    minHeight: '100vh',
    paddingBottom: '3rem',
  } as React.CSSProperties,

  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: '12px',
    marginBottom: '20px',
  } as React.CSSProperties,

  kpiCard: {
    position: 'relative' as const,
    overflow: 'hidden',
  } as React.CSSProperties,

  kpiAccent: (color: string) => ({
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '3px',
    height: '100%',
    background: color,
    borderRadius: '3px 0 0 3px',
  }),

  mainLayout: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '0',
    transition: 'grid-template-columns 0.25s ease',
  } as React.CSSProperties,

  mainLayoutWithPanel: {
    display: 'grid',
    gridTemplateColumns: '62% 38%',
    gap: '16px',
    alignItems: 'stretch',
  } as React.CSSProperties,

  thCell: {
    padding: '10px 12px',
    fontSize: '0.6875rem',
    fontWeight: 600,
    color: '#8a8580',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap' as const,
    borderBottom: '2px solid #dbd9d7',
    background: '#fafaf8',
  } as React.CSSProperties,

  tdCell: {
    padding: '12px 12px',
    fontSize: '0.8125rem',
    verticalAlign: 'middle' as const,
    borderBottom: '1px solid #edebea',
  } as React.CSSProperties,

  row: (isSelected: boolean, isActive: boolean, disabled: boolean) => ({
    cursor: 'pointer',
    transition: 'background 0.15s ease',
    background: isActive ? '#ebf3ff' : isSelected ? '#f0f8ff' : 'transparent',
    opacity: disabled ? 0.55 : 1,
  }),

  badge: (bg: string, color: string) => ({
    display: 'inline-block',
    fontSize: '0.625rem',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '10px',
    lineHeight: '1.4',
    background: bg,
    color,
    whiteSpace: 'nowrap' as const,
  }),

  flagBadge: {
    display: 'inline-block',
    fontSize: '0.5625rem',
    fontWeight: 600,
    padding: '1px 7px',
    borderRadius: '10px',
    background: '#fff3e0',
    color: '#c67320',
    marginRight: '3px',
    marginTop: '2px',
    whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,

  confidenceTrack: {
    width: '100%',
    height: '4px',
    background: '#edebea',
    borderRadius: '2px',
    marginTop: '3px',
  } as React.CSSProperties,

  confidenceFill: (pct: number, color: string) => ({
    width: `${pct}%`,
    height: '4px',
    background: color,
    borderRadius: '2px',
    transition: 'width 0.3s ease',
  }),

  expandSection: {
    padding: '16px 16px 16px 48px',
    background: '#fafaf8',
    borderBottom: '2px solid #edebea',
  } as React.CSSProperties,

  detailTh: {
    padding: '6px 10px',
    fontSize: '0.625rem',
    fontWeight: 600,
    color: '#8a8580',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
    borderBottom: '1px solid #dbd9d7',
    background: '#f7f5f5',
    whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,

  detailTd: {
    padding: '8px 10px',
    fontSize: '0.75rem',
    borderBottom: '1px solid #edebea',
    verticalAlign: 'top' as const,
  } as React.CSSProperties,

  sidePanel: {
    position: 'sticky' as const,
    top: '12px',
    maxHeight: 'calc(100vh - 24px)',
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
    background: '#fff',
    borderRadius: '6px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    border: '1px solid #e5e4e2',
  } as React.CSSProperties,

  sidePanelHeader: {
    padding: '16px 20px 12px',
    borderBottom: '1px solid #edebea',
    background: '#fff',
  } as React.CSSProperties,

  sidePanelBody: {
    flex: 1,
    overflowY: 'auto' as const,
    background: '#fff',
  } as React.CSSProperties,

  metaRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    padding: '6px 0',
    borderBottom: '1px solid #f5f3f2',
    fontSize: '0.75rem',
  } as React.CSSProperties,

  metaLabel: {
    color: '#8a8580',
    fontSize: '0.6875rem',
    flexShrink: 0,
    marginRight: '12px',
  } as React.CSSProperties,

  metaValue: {
    color: '#252525',
    fontSize: '0.75rem',
    fontWeight: 500,
    textAlign: 'right' as const,
    wordBreak: 'break-all' as const,
  } as React.CSSProperties,

  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 20px',
    flexWrap: 'wrap' as const,
  } as React.CSSProperties,

  summaryFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    marginTop: '8px',
  } as React.CSSProperties,

  noReceiptBanner: {
    padding: '12px 16px',
    background: '#f5f3f2',
    border: '1px solid #e5e4e2',
    borderRadius: '8px',
    fontSize: '0.75rem',
    color: '#6b6560',
    display: 'flex',
    gap: '10px',
    alignItems: 'flex-start',
  } as React.CSSProperties,

  noReceiptInline: {
    padding: '10px 12px',
    background: '#fafafa',
    border: '1px solid #edebea',
    borderRadius: '6px',
    color: '#6b6560',
    fontSize: '0.75rem',
  } as React.CSSProperties,
};

// ---------------------------------------------------------------------------
// Parse URL hash for target_payment_month
// ---------------------------------------------------------------------------

function parseMonthHash(): string | null {
  if (typeof window === 'undefined') return null;
  const h = window.location.hash.toLowerCase();
  const m = h.match(/fixed\/(\d{4})-(\d{2})/);
  if (!m) return null;
  return `${m[1]}-${m[2]}`;
}

interface Toast {
  id: number;
  kind: 'success' | 'error' | 'notice' | 'info';
  text: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const FixedJournalVerificationPage: React.FC = () => {
  // Month selection (target_payment_month)
  const [targetMonth, setTargetMonth] = useState<string>(() => parseMonthHash() || currentYyyyMm());

  // Filter / selection / expand / sidepanel state
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [activePanelId, setActivePanelId] = useState<string | null>(null);
  const [sidePanelTab, setSidePanelTab] = useState(0);

  // Data
  const [entries, setEntries] = useState<FixedEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastLoadedAt, setLastLoadedAt] = useState<string | null>(null);

  // Registration / skip progress
  const [bulkRegistering, setBulkRegistering] = useState(false);
  const [registeringIds, setRegisteringIds] = useState<Set<string>>(new Set());

  // Toasts
  const [toasts, setToasts] = useState<Toast[]>([]);
  const pushToast = useCallback((t: Omit<Toast, 'id'>) => {
    const id = Date.now() + Math.floor(Math.random() * 1000);
    setToasts((prev) => [...prev, { ...t, id }]);
    setTimeout(() => setToasts((prev) => prev.filter((x) => x.id !== id)), 6000);
  }, []);

  // -------------------------------------------------------------------------
  // Load fixed-recurring previews + all partners (including disabled)
  // -------------------------------------------------------------------------
  const loadEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // (1) preview (enabled only) — gives computed issue/due/target_payment_month + deal details
      const previewRes = await previewFixedRecurring(targetMonth);
      // (2) fetch all partners (enabled + disabled) to show disabled rows greyed
      let allPartners: FixedRecurringPartner[] = [];
      try {
        // GET /api/fixed-recurring/partners?enabled=0 returns all
        const url = `${(await import('../api/client')).API_BASE}/api/fixed-recurring/partners?enabled=0`;
        const r = await fetch(url);
        if (r.ok) {
          const j = await r.json();
          allPartners = (j.items || []) as FixedRecurringPartner[];
        }
      } catch {
        try {
          const j = await fetchFixedRecurringPartners();
          allPartners = j.items;
        } catch {/* noop */}
      }

      const previews = previewRes.previews;
      const previewByPid = new Map<number, FixedRecurringPreview>();
      previews.forEach((pv) => previewByPid.set(pv.partner_id, pv));

      const rows: FixedEntry[] = [];
      // preview 由来: enabled の行
      previews.forEach((pv) => {
        rows.push(previewToEntry(pv, targetMonth));
      });
      // preview に含まれない disabled partner を足す (UI 表示)
      allPartners.forEach((p) => {
        if (!previewByPid.has(p.partner_id) && (p.enabled ?? 1) === 0) {
          // Build a virtual preview-like shape from the partner row itself
          const fakePreview: FixedRecurringPreview = {
            partner_id: p.partner_id,
            partner_name: p.partner_name,
            monthly_amount: p.monthly_amount,
            amount_stddev: p.amount_stddev,
            confidence: p.confidence,
            source: p.source,
            issue_day_pattern: p.issue_day_pattern,
            due_day_pattern: p.due_day_pattern,
            ref_number: `fixed:${targetMonth}:${p.partner_id}`,
            issue_date: '',
            due_date: '',
            target_payment_month: null,
            proposed_deal_json: p.template_json || {},
            already_registered_month: p.last_registered_month === targetMonth,
            existing_freee_deal_id: null,
            existing_proposal_status: null,
            last_registered_month: p.last_registered_month,
            receipt_presence_ratio: p.receipt_presence_ratio,
            evidence_json: p.evidence_json,
            enabled: p.enabled,
          };
          rows.push(previewToEntry(fakePreview, targetMonth));
        }
      });

      rows.sort((a, b) => {
        // user_specified first, then by monthly_amount desc
        if (a.source !== b.source) return a.source === 'user_specified' ? -1 : 1;
        return b.monthlyAmount - a.monthlyAmount;
      });
      setEntries(rows);
      setLastLoadedAt(new Date().toISOString().slice(0, 19).replace('T', ' '));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [targetMonth]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  // -------------------------------------------------------------------------
  // Derived state
  // -------------------------------------------------------------------------
  const filtered = useMemo(() => {
    return entries.filter((e) => {
      if (statusFilter !== 'all' && e.status !== statusFilter) return false;
      if (sourceFilter !== 'all' && e.source !== sourceFilter) return false;
      if (searchText) {
        const q = searchText.toLowerCase();
        const searchable = [e.partnerName, e.storeName].filter(Boolean).join(' ').toLowerCase();
        if (!searchable.includes(q)) return false;
      }
      return true;
    });
  }, [entries, statusFilter, sourceFilter, searchText]);

  const stats = useMemo(() => {
    const total = entries.length;
    const registered = entries.filter((e) => e.status === 'registered').length;
    const pending = entries.filter((e) => e.status === 'pending').length;
    const skipped = entries.filter((e) => e.status === 'skipped').length;
    const disabled = entries.filter((e) => e.status === 'disabled').length;
    const totalAmount = entries
      .filter((e) => e.status === 'pending' || e.status === 'registered')
      .reduce((s, e) => s + e.monthlyAmount, 0);
    return { total, registered, pending, skipped, disabled, totalAmount };
  }, [entries]);

  const activeEntry = useMemo(
    () => (activePanelId ? entries.find((e) => e.id === activePanelId) ?? null : null),
    [entries, activePanelId],
  );

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------
  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) =>
      prev.size === filtered.length ? new Set() : new Set(filtered.filter((e) => e.status === 'pending').map((e) => e.id)),
    );
  }, [filtered]);

  const handleRowClick = useCallback(
    (entry: FixedEntry) => {
      if (activePanelId === entry.id) {
        setActivePanelId(null);
      } else {
        setActivePanelId(entry.id);
        setSidePanelTab(0);
      }
    },
    [activePanelId],
  );

  const handleExpandToggle = useCallback(
    (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setExpandedId((prev) => (prev === id ? null : id));
    },
    [],
  );

  // 単一行登録 — bulk-register に 1 件配列で投げる
  const onRegister = useCallback(async (id: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    const entry = entries.find((x) => x.id === id);
    if (!entry) return;
    if (entry.status !== 'pending') {
      pushToast({ kind: 'info', text: `${entry.partnerName}: 既に ${statusToLabel(entry.status)}` });
      return;
    }
    try {
      setRegisteringIds((s) => { const n = new Set(s); n.add(id); return n; });
      let sid = '';
      try {
        const sess = await openSessionForFixedRecurring();
        if (sess && sess.session_id) sid = sess.session_id;
      } catch { /* worker fallback OK */ }
      const res = await bulkRegisterFixedRecurring(sid, targetMonth, [entry.partnerId]);
      const r = res.results?.[0];
      if (r?.status === 'queued') {
        pushToast({ kind: 'success', text: `${entry.partnerName}: 登録キューに追加 (ref=${r.ref_number})` });
      } else if (r?.status === 'skipped') {
        pushToast({ kind: 'info', text: `${entry.partnerName}: スキップ (${r.reason || 'reason unknown'})` });
      } else {
        pushToast({ kind: 'error', text: `${entry.partnerName}: ${r?.reason || '失敗'}` });
      }
      // Claude 側 consumer が freee に反映するまで少し待ってから再 fetch
      setTimeout(() => { loadEntries(); }, 3500);
    } catch (err: unknown) {
      pushToast({ kind: 'error', text: err instanceof Error ? err.message : String(err) });
    } finally {
      setRegisteringIds((s) => { const n = new Set(s); n.delete(id); return n; });
    }
  }, [entries, targetMonth, pushToast, loadEntries]);

  // 単一行スキップ — 新規 endpoint skip-month で last_registered_month 上書き
  const onSkip = useCallback(async (id: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    const entry = entries.find((x) => x.id === id);
    if (!entry) return;
    if (!window.confirm(`${entry.partnerName} を ${targetMonth} のみスキップします。よろしいですか?`)) return;
    try {
      setRegisteringIds((s) => { const n = new Set(s); n.add(id); return n; });
      await skipFixedRecurringMonth(entry.partnerId, targetMonth);
      pushToast({ kind: 'success', text: `${entry.partnerName}: ${targetMonth} をスキップしました` });
      await loadEntries();
    } catch (err: unknown) {
      pushToast({ kind: 'error', text: err instanceof Error ? err.message : String(err) });
    } finally {
      setRegisteringIds((s) => { const n = new Set(s); n.delete(id); return n; });
    }
  }, [entries, targetMonth, pushToast, loadEntries]);

  // 一括登録 — 選択 >0 なら選択分、選択==0 なら filtered の pending 全部
  const onBulkRegister = useCallback(async () => {
    const targetEntries = selectedIds.size > 0
      ? filtered.filter((e) => selectedIds.has(e.id) && e.status === 'pending')
      : filtered.filter((e) => e.status === 'pending');
    if (targetEntries.length === 0) {
      pushToast({ kind: 'info', text: '登録対象の未登録行がありません' });
      return;
    }
    if (!window.confirm(`${targetEntries.length}件の固定仕訳を ${targetMonth} 分として登録キューに追加します。よろしいですか?`)) return;
    try {
      setBulkRegistering(true);
      let sid = '';
      try {
        const sess = await openSessionForFixedRecurring();
        if (sess && sess.session_id) sid = sess.session_id;
      } catch { /* worker fallback OK */ }
      const pids = targetEntries.map((e) => e.partnerId);
      const res = await bulkRegisterFixedRecurring(sid, targetMonth, pids);
      pushToast({
        kind: res.failed > 0 ? 'error' : (res.queued > 0 ? 'success' : 'info'),
        text: `一括登録: queued=${res.queued} / skipped=${res.skipped} / failed=${res.failed}`,
      });
      setSelectedIds(new Set());
      setTimeout(() => { loadEntries(); }, 3500);
    } catch (err: unknown) {
      pushToast({ kind: 'error', text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBulkRegistering(false);
    }
  }, [filtered, selectedIds, targetMonth, pushToast, loadEntries]);

  // 一括スキップ — 選択された pending 行すべての last_registered_month を targetMonth に
  const onBulkSkip = useCallback(async () => {
    const targetEntries = filtered.filter((e) => selectedIds.has(e.id) && e.status === 'pending');
    if (targetEntries.length === 0) {
      pushToast({ kind: 'info', text: 'スキップ対象の未登録行がありません' });
      return;
    }
    if (!window.confirm(`${targetEntries.length}件を ${targetMonth} のみスキップします。よろしいですか?`)) return;
    try {
      setBulkRegistering(true);
      let ok = 0, fail = 0;
      for (const e of targetEntries) {
        try {
          await skipFixedRecurringMonth(e.partnerId, targetMonth);
          ok += 1;
        } catch (err) {
          console.error('[bulk-skip] failed:', e.partnerName, err);
          fail += 1;
        }
      }
      pushToast({
        kind: fail > 0 ? 'error' : 'success',
        text: `一括スキップ: ok=${ok} / failed=${fail}`,
      });
      setSelectedIds(new Set());
      await loadEntries();
    } catch (err: unknown) {
      pushToast({ kind: 'error', text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBulkRegistering(false);
    }
  }, [filtered, selectedIds, targetMonth, pushToast, loadEntries]);

  // -------------------------------------------------------------------------
  // Month selector options (targetPayment month bucket)
  // -------------------------------------------------------------------------
  const monthOptions = useMemo(() => {
    const now = currentYyyyMm();
    const months: string[] = [];
    for (let i = -6; i <= 1; i += 1) months.push(monthOffset(now, i));
    // ensure current targetMonth is included
    if (!months.includes(targetMonth)) months.push(targetMonth);
    months.sort();
    return months.map((m) => ({ name: `${m}`, value: m }));
  }, [targetMonth]);

  // -------------------------------------------------------------------------
  // Render helpers (cloned from legacy)
  // -------------------------------------------------------------------------
  const renderKpiCard = (
    label: string,
    value: string,
    accentColor: string,
    subtext?: string,
  ) => (
    <CardBase paddingSize="small" overflowHidden={false}>
      <div style={S.kpiCard}>
        <div style={S.kpiAccent(accentColor)} />
        <div style={{ paddingLeft: '8px' }}>
          <div style={{ fontSize: '0.6875rem', color: '#8a8580', marginBottom: '4px', fontWeight: 500 }}>
            {label}
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#252525', lineHeight: 1.1 }}>
            {value}
          </div>
          {subtext && (
            <div style={{ fontSize: '0.625rem', color: '#8a8580', marginTop: '4px' }}>{subtext}</div>
          )}
        </div>
      </div>
    </CardBase>
  );

  const renderConfidence = (c: number) => {
    const pct = Math.round(c * 100);
    const color = confidenceColor(c);
    return (
      <div style={{ minWidth: '48px' }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, color, textAlign: 'center' }}>
          {pct}%
        </div>
        <div style={S.confidenceTrack}>
          <div style={S.confidenceFill(pct, confidenceBarColor(c))} />
        </div>
      </div>
    );
  };

  const renderPatternBadge = (status: string) => {
    const cfg = PATTERN_STATUS_LABELS[status] ?? PATTERN_STATUS_LABELS.Unknown;
    return <span style={S.badge(cfg.bg, cfg.color)}>{cfg.label}</span>;
  };

  const renderDetailTable = (entry: FixedEntry) => (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={S.detailTh}>借方科目</th>
          <th style={{ ...S.detailTh, textAlign: 'right' }}>金額</th>
          <th style={{ ...S.detailTh, textAlign: 'right' }}>税額</th>
          <th style={S.detailTh}>税区分</th>
          <th style={S.detailTh}>部門</th>
          <th style={S.detailTh}>品目</th>
          <th style={S.detailTh}>摘要</th>
        </tr>
      </thead>
      <tbody>
        {entry.details.map((d) => (
          <tr key={d.index}>
            <td style={{ ...S.detailTd, fontWeight: 600, color: '#252525' }}>{d.accountName}</td>
            <td style={{ ...S.detailTd, textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {fmt(d.amount)}
            </td>
            <td style={{ ...S.detailTd, textAlign: 'right', color: '#8a8580', fontVariantNumeric: 'tabular-nums' }}>
              {fmt(d.taxAmount)}
            </td>
            <td style={S.detailTd}>
              <span style={S.badge('#f5f3f2', '#6b6560')}>{d.taxCodeName}</span>
            </td>
            <td style={S.detailTd}>
              <span style={{ color: '#252525', fontWeight: 500 }}>{d.sectionName}</span>
            </td>
            <td style={S.detailTd}>
              {d.itemName ? (
                <span style={{ color: '#252525' }}>{d.itemName}</span>
              ) : (
                <span style={{ color: '#c4c0bc' }}>--</span>
              )}
            </td>
            <td style={{ ...S.detailTd, maxWidth: '200px' }}>
              <span style={{ color: '#6b6560', fontSize: '0.6875rem' }}>{d.description}</span>
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td
            colSpan={7}
            style={{
              padding: '8px 10px',
              background: '#f7f5f5',
              fontSize: '0.6875rem',
              color: '#6b6560',
            }}
          >
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600, color: '#252525' }}>合計 (月額): </span>
              <span style={{ marginLeft: 'auto', fontWeight: 600, color: '#252525' }}>
                {fmt(entry.monthlyAmount)}
              </span>
            </div>
          </td>
        </tr>
      </tfoot>
    </table>
  );

  const renderNoReceiptBanner = (entry: FixedEntry) => {
    const ratio = entry.receiptPresenceRatio ?? 0;
    const pct = Math.round(ratio * 100);
    const warn = ratio > 0.3;
    return (
      <div
        style={{
          ...S.noReceiptBanner,
          background: warn ? '#fff4e5' : '#f5f3f2',
          borderColor: warn ? '#f3c28f' : '#e5e4e2',
          color: warn ? '#8b5000' : '#6b6560',
        }}
      >
        <div style={{ fontSize: '1rem', lineHeight: 1 }}>📄</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, marginBottom: '4px' }}>
            証憑なしテンプレート
          </div>
          <div style={{ lineHeight: 1.5 }}>
            この取引は固定金額の継続取引です。<br />
            過去 receipt 率: {pct}% / 最終登録: {entry.lastRegisteredMonth || '—'} / 次回: {targetMonth}
            {entry.sampleMonths.length > 0 && (
              <div style={{ marginTop: '4px' }}>
                sample_months: [{entry.sampleMonths.join(', ')}]
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSidePanel = () => {
    if (!activeEntry) return null;
    return (
      <div style={{ minHeight: '1px' }}>
        <div style={S.sidePanel}>
          {/* Panel header */}
          <div style={S.sidePanelHeader}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text weight="bold" size={0.875}>
                  {activeEntry.partnerName}
                </Text>
                <div style={{ marginTop: '4px' }}>
                  <Text size={0.75} color="burnt">
                    pid:{activeEntry.partnerId}
                  </Text>
                  <span style={{ margin: '0 6px', color: '#dbd9d7' }}>|</span>
                  <StatusIcon type={statusToIcon(activeEntry.status)}>
                    {statusToLabel(activeEntry.status)}
                  </StatusIcon>
                </div>
              </div>
              <Button
                small
                appearance="tertiary"
                onClick={() => setActivePanelId(null)}
              >
                閉じる
              </Button>
            </div>
            <div style={{ marginTop: '8px' }}>
              <TabBar
                tabs={['テンプレート', 'evidence', 'メタデータ']}
                currentTabIndex={sidePanelTab}
                onClickTab={(i) => setSidePanelTab(i)}
                small
              />
            </div>
          </div>

          {/* Panel body */}
          <div style={S.sidePanelBody}>
            {/* テンプレート tab (証憑なし bannr + detail table) */}
            {sidePanelTab === 0 && (
              <div style={{ padding: '16px 20px' }}>
                {renderNoReceiptBanner(activeEntry)}
                <div style={{ marginTop: '14px' }}>
                  <SubSectionTitle headlineLevel={4}>仕訳明細</SubSectionTitle>
                  <ColumnBase paddingSize="zero" border="default" rounded>
                    {renderDetailTable(activeEntry)}
                  </ColumnBase>
                </div>

                {/* Quick actions */}
                <div style={{ marginTop: '12px' }}>
                  <HStack gap={0.5}>
                    <Button
                      appearance="primary"
                      small
                      disabled={activeEntry.status !== 'pending' || registeringIds.has(activeEntry.id) || bulkRegistering}
                      onClick={() => onRegister(activeEntry.id)}
                    >
                      {registeringIds.has(activeEntry.id) ? '登録中…' : '今月の登録 (1-click)'}
                    </Button>
                    <Button
                      appearance="secondary"
                      small
                      danger
                      disabled={activeEntry.status !== 'pending' || registeringIds.has(activeEntry.id) || bulkRegistering}
                      onClick={() => onSkip(activeEntry.id)}
                    >
                      今月スキップ
                    </Button>
                  </HStack>
                </div>
              </div>
            )}

            {/* evidence tab */}
            {sidePanelTab === 1 && (
              <div style={{ padding: '16px 20px' }}>
                <VStack gap={0.25}>
                  <SubSectionTitle headlineLevel={4}>学習根拠</SubSectionTitle>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>過去 receipt 率</span>
                    <span style={S.metaValue}>
                      {Math.round((activeEntry.receiptPresenceRatio ?? 0) * 100)}%
                    </span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>月額変動 (stddev)</span>
                    <span style={S.metaValue}>
                      {activeEntry.amountStddev == null ? '—' : `±¥${Math.round(activeEntry.amountStddev).toLocaleString('ja-JP')}`}
                    </span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>確証タイプ</span>
                    <span style={S.metaValue}>
                      {activeEntry.source === 'user_specified' ? 'ユーザ確証' : '動的学習'}
                    </span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>最終登録</span>
                    <span style={S.metaValue}>{activeEntry.lastRegisteredMonth || '—'}</span>
                  </div>
                </VStack>
                <div style={{ marginTop: '16px' }}>
                  <SubSectionTitle headlineLevel={4}>sample_months</SubSectionTitle>
                  {activeEntry.sampleMonths.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                      {activeEntry.sampleMonths.map((m) => (
                        <span key={m} style={S.badge('#e8f0fe', '#2e7cf6')}>{m}</span>
                      ))}
                    </div>
                  ) : (
                    <Note>サンプル月なし</Note>
                  )}
                </div>
                <div style={{ marginTop: '16px' }}>
                  <SubSectionTitle headlineLevel={4}>evidence_json (raw)</SubSectionTitle>
                  <pre
                    style={{
                      marginTop: '4px',
                      padding: '10px 12px',
                      background: '#f7f5f5',
                      borderRadius: '6px',
                      fontSize: '0.6875rem',
                      color: '#6b6560',
                      whiteSpace: 'pre-wrap',
                      overflowX: 'auto',
                    }}
                  >
                    {JSON.stringify(activeEntry.evidenceJson || {}, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {/* メタデータ tab */}
            {sidePanelTab === 2 && (
              <div style={{ padding: '16px 20px' }}>
                <VStack gap={0.25}>
                  <SubSectionTitle headlineLevel={4}>テンプレート情報</SubSectionTitle>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>取引先</span>
                    <span style={S.metaValue}>{activeEntry.partnerName}</span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>partner_id</span>
                    <span style={S.metaValue}>{activeEntry.partnerId}</span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>ref_number</span>
                    <span style={S.metaValue}>{activeEntry.refNumber}</span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>発生日 ({patternDescription(activeEntry.issueDayPattern)})</span>
                    <span style={S.metaValue}>{activeEntry.issueDate || '—'}</span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>支払期日 ({patternDescription(activeEntry.dueDayPattern)})</span>
                    <span style={S.metaValue}>{activeEntry.dueDate || '—'}</span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>支払月 (target_payment_month)</span>
                    <span style={S.metaValue}>{activeEntry.targetPaymentMonth || '—'}</span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>月額</span>
                    <span style={{ ...S.metaValue, fontWeight: 700, fontSize: '0.875rem' }}>{fmt(activeEntry.monthlyAmount)}</span>
                  </div>
                </VStack>

                <div style={{ marginTop: '16px' }}>
                  <SubSectionTitle headlineLevel={4}>学習</SubSectionTitle>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>パターン</span>
                    <span>{renderPatternBadge(patternForConfidence(activeEntry.confidence))}</span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>信頼度</span>
                    <span style={{ ...S.metaValue, color: confidenceColor(activeEntry.confidence), fontWeight: 700 }}>
                      {Math.round(activeEntry.confidence * 100)}%
                    </span>
                  </div>
                </div>

                {activeEntry.flags.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <SubSectionTitle headlineLevel={4}>フラグ</SubSectionTitle>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                      {activeEntry.flags.map((f, i) => (
                        <span key={i} style={S.badge('#fff3e0', '#c67320')}>{f}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // -------------------------------------------------------------------------
  // Main render
  // -------------------------------------------------------------------------
  const registeredPct = stats.total > 0 ? Math.round((stats.registered / stats.total) * 100) : 0;

  return (
    <div style={S.page}>
      <Container width="wide">
        {/* Breadcrumbs */}
        <div style={{ padding: '16px 0 8px' }}>
          <Breadcrumbs
            links={[
              { title: 'FREEEMAN', url: '/' },
              { title: '定額仕訳登録' },
              { title: targetMonth },
            ]}
          />
        </div>

        {/* Page Title */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <HStack gap={1} alignItems="center">
              <PageTitle>
                定額仕訳登録
              </PageTitle>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={S.badge('#e8f0fe', '#2e7cf6')}>
                  {targetMonth}
                </span>
                <span style={S.badge('#f0f9ff', '#0284c7')}>
                  定額継続
                </span>
                <span style={S.badge('#fff4e5', '#c67320')}>
                  証憑なし一括登録
                </span>
              </div>
            </HStack>
            <HStack gap={0.5}>
              <SelectBox
                options={monthOptions}
                value={targetMonth}
                onChange={(e) => {
                  const v = e.target.value;
                  setTargetMonth(v);
                  window.location.hash = `#fixed/${v}`;
                }}
                width="small"
                small
                label="支払月"
              />
              <Button
                appearance="primary"
                disabled={stats.pending === 0 || bulkRegistering}
                onClick={onBulkRegister}
              >
                {bulkRegistering
                  ? '一括登録中…'
                  : `対象月の固定取引を一括登録 (${stats.pending}件${stats.registered > 0 ? ` / 既登録${stats.registered}件はスキップ` : ''})`}
              </Button>
              <Button appearance="secondary" small onClick={loadEntries} disabled={loading}>
                {loading ? '読込中…' : '再読込'}
              </Button>
            </HStack>
          </div>
          <div style={{ marginTop: '4px' }}>
            <Note>
              支払月 {targetMonth} / 登録対象 {stats.total}件 / 対応待ち(今月登録) {stats.pending}件 (この表で登録実行)
              {lastLoadedAt && ` / 最終更新 ${lastLoadedAt}`}
            </Note>
          </div>
        </div>

        {/* Error message */}
        {error && <MessageBlock error mb={0.5}>{error}</MessageBlock>}

        {/* KPI Cards */}
        <div style={S.kpiGrid}>
          {renderKpiCard('登録対象', `${stats.total}件`, '#2e7cf6', `${filtered.length}件表示中`)}
          {renderKpiCard('月額合計', fmt(stats.totalAmount), '#252525')}
          {renderKpiCard('今月登録済', `${stats.registered}件`, '#338650', `${registeredPct}%`)}
          {renderKpiCard('対応待ち(今月登録)', `${stats.pending}件`, '#a46a12')}
          {renderKpiCard('除外(対象外/スキップ)', `${stats.disabled + stats.skipped}件`, '#8a8580')}
        </div>

        {/* Progress bar */}
        {stats.total > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <ProgressBar value={stats.registered} maxValue={stats.total} width="full" ariaLabel="登録進捗" />
              </div>
              <Text size={0.75} color="burnt">
                {stats.registered}/{stats.total} 登録済
              </Text>
            </div>
          </div>
        )}

        {/* Filter Bar */}
        <CardBase paddingSize="zero" mb={0.5}>
          <div style={S.filterBar}>
            <SearchField
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="取引先名・部門で検索"
              width="large"
              small
            />
            <SelectBox
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              width="small"
              small
              label="ステータス"
            />
            <SelectBox
              options={SOURCE_OPTIONS}
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              width="medium"
              small
              label="ソース"
            />
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Note>{filtered.length}件</Note>
            </div>
          </div>
        </CardBase>

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <MessageBlock success mb={0.5}>
            <HStack gap={1} alignItems="center">
              <Text weight="bold" size={0.875}>
                {selectedIds.size}件選択中
              </Text>
              <Button
                appearance="primary"
                small
                disabled={bulkRegistering}
                onClick={onBulkRegister}
              >
                {bulkRegistering ? '登録中…' : '選択して一括登録'}
              </Button>
              <Button
                appearance="secondary"
                small
                danger
                disabled={bulkRegistering}
                onClick={onBulkSkip}
              >
                選択を今月スキップ
              </Button>
              <Button appearance="tertiary" small onClick={() => setSelectedIds(new Set())}>
                選択解除
              </Button>
            </HStack>
          </MessageBlock>
        )}

        {/* Main two-column layout */}
        <div style={activePanelId ? S.mainLayoutWithPanel : S.mainLayout}>
          {/* LEFT: Table */}
          <CardBase paddingSize="zero" overflowHidden={false}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '820px' }}>
                <thead>
                  <tr>
                    <th style={{ ...S.thCell, width: '36px', textAlign: 'center' }}>
                      <CheckBox
                        checked={selectedIds.size === filtered.length && filtered.length > 0}
                        onChange={toggleSelectAll}
                        small
                      />
                    </th>
                    <th style={{ ...S.thCell, width: '32px', textAlign: 'center' }}>#</th>
                    <th style={S.thCell}>取引先</th>
                    <th style={{ ...S.thCell, textAlign: 'right' }}>月額</th>
                    <th style={S.thCell}>部門</th>
                    <th style={S.thCell}>科目</th>
                    <th style={{ ...S.thCell, textAlign: 'center', width: '64px' }}>信頼度</th>
                    <th style={{ ...S.thCell, textAlign: 'center', width: '80px' }}>パターン</th>
                    <th style={{ ...S.thCell, textAlign: 'center', width: '110px' }}>ステータス</th>
                    <th style={{ ...S.thCell, textAlign: 'center', width: '110px' }}>アクション</th>
                    <th style={{ ...S.thCell, textAlign: 'center', width: '40px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((entry, idx) => {
                    const rowDisabled = entry.status === 'disabled';
                    return (
                      <React.Fragment key={entry.id}>
                        {/* Main row */}
                        <tr
                          style={S.row(selectedIds.has(entry.id), activePanelId === entry.id, rowDisabled)}
                          onClick={() => handleRowClick(entry)}
                        >
                          <td
                            style={{ ...S.tdCell, textAlign: 'center', width: '36px' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <CheckBox
                              checked={selectedIds.has(entry.id)}
                              onChange={() => toggleSelect(entry.id)}
                              disabled={entry.status !== 'pending'}
                              small
                            />
                          </td>
                          <td style={{ ...S.tdCell, textAlign: 'center', width: '32px' }}>
                            <span style={{ fontSize: '0.6875rem', color: '#8a8580', fontWeight: 500 }}>
                              {idx + 1}
                            </span>
                          </td>
                          <td style={S.tdCell}>
                            <div>
                              <span style={{ fontWeight: 600, color: '#252525', fontSize: '0.8125rem' }}>
                                {entry.partnerName}
                              </span>
                            </div>
                            {entry.flags.length > 0 && (
                              <div style={{ marginTop: '2px' }}>
                                {entry.flags.map((f, i) => (
                                  <span key={i} style={S.flagBadge}>{f}</span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td
                            style={{
                              ...S.tdCell,
                              textAlign: 'right',
                              fontWeight: 700,
                              fontSize: '0.8125rem',
                              fontVariantNumeric: 'tabular-nums',
                              color: '#252525',
                            }}
                          >
                            {fmt(entry.monthlyAmount)}
                            {(entry.amountStddev ?? 0) > 0 && (
                              <div style={{ fontSize: '0.625rem', fontWeight: 400, color: '#8a8580' }}>
                                σ=±¥{Math.round(entry.amountStddev || 0).toLocaleString('ja-JP')}
                              </div>
                            )}
                          </td>
                          <td style={S.tdCell}>
                            <span style={{ fontSize: '0.75rem', color: '#6b6560' }}>
                              {entry.storeName}
                            </span>
                          </td>
                          <td style={S.tdCell}>
                            <div>
                              <span style={{ fontSize: '0.75rem', color: '#252525', fontWeight: 500 }}>
                                {entry.details[0]?.accountName || '—'}
                              </span>
                            </div>
                            {entry.details.length > 1 && (
                              <span
                                style={{
                                  fontSize: '0.625rem',
                                  color: '#8a8580',
                                  background: '#f5f3f2',
                                  padding: '0 5px',
                                  borderRadius: '8px',
                                }}
                              >
                                +{entry.details.length - 1}行
                              </span>
                            )}
                          </td>
                          <td style={{ ...S.tdCell, textAlign: 'center' }}>
                            {renderConfidence(entry.confidence)}
                          </td>
                          <td style={{ ...S.tdCell, textAlign: 'center' }}>
                            {renderPatternBadge(patternForConfidence(entry.confidence))}
                          </td>
                          <td style={{ ...S.tdCell, textAlign: 'center' }}>
                            <VStack gap={0.25} alignItems="center">
                              <StatusIcon type={statusToIcon(entry.status)}>
                                {statusToLabel(entry.status)}
                              </StatusIcon>
                              {entry.alreadyRegistered && entry.existingFreeeDealId && (
                                <a
                                  href={`https://secure.freee.co.jp/deals#deal_id=${entry.existingFreeeDealId}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    ...S.badge('#e8f5e9', '#2e7d32'),
                                    textDecoration: 'none',
                                    cursor: 'pointer',
                                  }}
                                >
                                  ✓ freeeで開く ↗
                                </a>
                              )}
                              {!entry.alreadyRegistered && entry.lastRegisteredMonth && (
                                <span style={S.badge('#f5f3f2', '#6b6560')}>
                                  直近登録 {entry.lastRegisteredMonth}
                                </span>
                              )}
                            </VStack>
                          </td>
                          <td
                            style={{ ...S.tdCell, textAlign: 'center', width: '110px' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              small
                              appearance="primary"
                              disabled={entry.status !== 'pending' || registeringIds.has(entry.id) || bulkRegistering}
                              onClick={(e) => onRegister(entry.id, e)}
                            >
                              {entry.alreadyRegistered
                                ? '✓'
                                : registeringIds.has(entry.id)
                                  ? '…'
                                  : '登録'}
                            </Button>
                          </td>
                          <td
                            style={{ ...S.tdCell, textAlign: 'center', width: '40px' }}
                            onClick={(e) => handleExpandToggle(entry.id, e)}
                          >
                            <span
                              style={{
                                display: 'inline-block',
                                fontSize: '0.75rem',
                                color: '#8a8580',
                                transform: expandedId === entry.id ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease',
                                cursor: 'pointer',
                                userSelect: 'none',
                              }}
                            >
                              &#9660;
                            </span>
                          </td>
                        </tr>

                        {/* Expanded detail row */}
                        {expandedId === entry.id && (
                          <tr>
                            <td colSpan={11} style={{ padding: 0 }}>
                              <div style={S.expandSection}>
                                {/* Detail header with section title and inline actions */}
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '10px',
                                  }}
                                >
                                  <SectionTitle headlineLevel={4}>
                                    仕訳明細 ({entry.details.length}行)
                                  </SectionTitle>
                                  <HStack gap={0.5}>
                                    <Button
                                      appearance="primary"
                                      small
                                      disabled={entry.status !== 'pending' || registeringIds.has(entry.id) || bulkRegistering}
                                      onClick={() => onRegister(entry.id)}
                                    >
                                      {registeringIds.has(entry.id) ? '登録中…' : '今月の登録'}
                                    </Button>
                                    <Button
                                      appearance="secondary"
                                      small
                                      danger
                                      disabled={entry.status !== 'pending' || registeringIds.has(entry.id) || bulkRegistering}
                                      onClick={() => onSkip(entry.id)}
                                    >
                                      今月スキップ
                                    </Button>
                                  </HStack>
                                </div>

                                {/* Registration dates summary (with pattern description) */}
                                <div
                                  style={{
                                    display: 'flex',
                                    gap: '16px',
                                    padding: '8px 12px',
                                    marginBottom: '6px',
                                    background: '#fafafa',
                                    border: '1px solid #e5e4e2',
                                    borderRadius: '4px',
                                    fontSize: '0.75rem',
                                    color: '#6b6560',
                                    flexWrap: 'wrap',
                                  }}
                                >
                                  <span>
                                    <b style={{ color: '#8a8580', fontWeight: 500 }}>発生日 ({patternDescription(entry.issueDayPattern)}):</b>{' '}
                                    <span style={{ color: '#252525', fontWeight: 600 }}>
                                      {entry.issueDate || '—'}
                                    </span>
                                  </span>
                                  <span>
                                    <b style={{ color: '#8a8580', fontWeight: 500 }}>支払期日 ({patternDescription(entry.dueDayPattern)}):</b>{' '}
                                    <span style={{ color: '#252525', fontWeight: 600 }}>
                                      {entry.dueDate || '—'}
                                    </span>
                                  </span>
                                  <span>
                                    <b style={{ color: '#8a8580', fontWeight: 500 }}>支払月:</b>{' '}
                                    <span style={{ color: '#252525', fontWeight: 600 }}>
                                      {entry.targetPaymentMonth || '—'}
                                    </span>
                                  </span>
                                  <span>
                                    <b style={{ color: '#8a8580', fontWeight: 500 }}>ref:</b>{' '}
                                    <span style={{ color: '#252525', fontFamily: 'monospace' }}>
                                      {entry.refNumber}
                                    </span>
                                  </span>
                                </div>

                                {/* Detail table */}
                                <ColumnBase paddingSize="zero" border="default" rounded>
                                  {renderDetailTable(entry)}
                                </ColumnBase>

                                {/* 証憑なし banner (inline, side panel 非表示時のみ — legacy の PdfViewer と同位置) */}
                                {!activePanelId && (
                                  <div style={{ marginTop: '12px' }}>
                                    <div style={S.noReceiptInline}>
                                      <div style={{ fontWeight: 600, color: '#252525', marginBottom: '4px' }}>
                                        証憑: 固定金額テンプレート / 証憑不要
                                      </div>
                                      <div>
                                        sample_months: [{entry.sampleMonths.join(', ') || '—'}]<br />
                                        receipt_presence_ratio: {Math.round((entry.receiptPresenceRatio ?? 0) * 100)}%
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}

                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={11} style={{ padding: '48px 24px', textAlign: 'center' }}>
                        <VStack gap={0.5} alignItems="center">
                          <Text size={0.875} color="burnt">
                            {loading ? '読込中…' : '条件に一致するテンプレートがありません'}
                          </Text>
                          <Note>フィルターまたは支払月を変更してください</Note>
                        </VStack>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardBase>

          {/* RIGHT: Side panel */}
          {activePanelId && renderSidePanel()}
        </div>

        {/* Footer summary */}
        <div style={S.summaryFooter}>
          <HStack gap={0.5} alignItems="center">
            {selectedIds.size > 0 && (
              <Button appearance="primary" disabled={bulkRegistering} onClick={onBulkRegister}>
                {bulkRegistering
                  ? '登録中…'
                  : `選択した${selectedIds.size}件を一括登録`}
              </Button>
            )}
          </HStack>
          <Note>
            支払月 {targetMonth} | {stats.registered}/{stats.total} 登録済 | 月額合計 {fmt(stats.totalAmount)}
          </Note>
        </div>
      </Container>

      {/* Floating toasts */}
      {toasts.map((t) => (
        <FloatingMessageBlock
          key={t.id}
          success={t.kind === 'success'}
          error={t.kind === 'error'}
          notice={t.kind === 'notice'}
          info={t.kind === 'info'}
          onClose={() =>
            setToasts((prev) => prev.filter((x) => x.id !== t.id))
          }
        >
          {t.text}
        </FloatingMessageBlock>
      ))}
    </div>
  );
};
